// src/features/calendar/settings/TemplatesSettings.tsx
// Reusable meeting shapes — event templates, surfaced in the create dialog.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useCalendarTemplate, useI18n } from '@react/hooks';
import { Button, Input, Textarea, Badge, Alert } from '@react/components';
import { Select } from '@/components/Select';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useScope } from '@/hooks/useScope';
import type { EventTemplate, EventType } from '@core/types/calendar';
import type { PaginatedRequest } from '@core/types/pagination';
import { ModalShell } from '../ModalShell';
import { errMessage, makeEntity } from '../time';

const EVENT_TYPES: EventType[] = [
  'EVENT_TYPE_EVENT', 'EVENT_TYPE_MEETING', 'EVENT_TYPE_FOCUS_TIME',
  'EVENT_TYPE_OUT_OF_OFFICE', 'EVENT_TYPE_APPOINTMENT',
];

export const TemplatesSettings = observer(() => {
  const { t } = useI18n();
  const scope = useScope();
  const store = useCalendarTemplate();

  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<EventTemplate | null>(null);
  const [deleting, setDeleting] = useState<EventTemplate | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [eventTitle, setEventTitle] = useState('');
  const [eventType, setEventType] = useState<EventType>('EVENT_TYPE_MEETING');
  const [eventDescription, setEventDescription] = useState('');
  const [locationName, setLocationName] = useState('');
  const [confUri, setConfUri] = useState('');
  const [isShared, setIsShared] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const load = () => {
    setError(null);
    store
      .ListEventTemplates({
        pagination: { cursor: '', limit: 100, direction: 'forward' },
      } as PaginatedRequest)
      .catch((e: unknown) => setError(errMessage(e, t('calendar.templates.loadError'))));
  };

  useEffect(() => { load(); }, []);

  const openDialog = async (listed: EventTemplate | null) => {
    let tpl = listed;
    if (listed?.entity?.id) {
      // Load the full template — the list payload may be trimmed.
      try {
        tpl = await store.GetEventTemplate(listed.entity.id);
      } catch {
        tpl = listed;
      }
    }
    setEditing(tpl);
    setName(tpl?.name ?? '');
    setDescription(tpl?.description ?? '');
    setEventTitle(tpl?.template_event?.title ?? '');
    setEventType(tpl?.template_event?.event_type ?? 'EVENT_TYPE_MEETING');
    setEventDescription(tpl?.template_event?.description ?? '');
    setLocationName(tpl?.template_event?.location?.display_name ?? '');
    setConfUri(tpl?.template_event?.conference?.uri ?? '');
    setIsShared(tpl?.is_shared ?? false);
    setDialogOpen(true);
  };

  const save = async () => {
    if (!name.trim()) return;
    setIsSaving(true);
    setError(null);
    try {
      const templateEvent = {
        ...(editing?.template_event ?? {
          title: '', is_all_day: false, timezone: '', is_recurring_instance: false,
          use_default_reminders: true, guest_can_modify: false, guest_can_invite_others: false,
          guest_can_see_other_guests: true, tags: [], event_type_id: '',
        }),
        title: eventTitle.trim() || name.trim(),
        event_type: eventType,
        description: eventDescription.trim() || undefined,
        location: locationName.trim() ? { display_name: locationName.trim() } : undefined,
        conference: confUri.trim() ? { type: 'CONFERENCE_TYPE_CUSTOM' as const, uri: confUri.trim(), phone_numbers: [] } : undefined,
      };
      if (editing) {
        await store.UpdateEventTemplate(editing.entity?.id ?? '', {
          template: { ...editing, name: name.trim(), description: description || undefined, is_shared: isShared, template_event: templateEvent },
          field_mask: ['name', 'description', 'is_shared', 'template_event'],
        });
      } else {
        await store.CreateEventTemplate({
          template: {
            entity: makeEntity('', 'event_template', scope),
            name: name.trim(),
            description: description || undefined,
            is_shared: isShared,
            auto_create: false,
            usage_count: 0,
            template_event: templateEvent,
          },
        });
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      setError(errMessage(e, t('calendar.templates.saveError')));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t('calendar.templates.hint')}</p>
        <Button size="sm" onClick={() => openDialog(null)}>
          {t('calendar.templates.newTemplate')}
        </Button>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {store.isLoading && store.items.length === 0 && (
        <div className="h-32 animate-pulse rounded-md bg-muted" aria-busy="true" />
      )}

      {!store.isLoading && store.items.length === 0 && (
        <div className="rounded-md border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">{t('calendar.templates.empty')}</p>
          <Button size="sm" className="mt-3" onClick={() => openDialog(null)}>
            {t('calendar.templates.newTemplate')}
          </Button>
        </div>
      )}

      <div className="space-y-2">
        {store.items.map((tpl) => (
          <div key={tpl.entity?.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate font-medium text-foreground">{tpl.name}</span>
                {tpl.is_shared && <Badge variant="secondary">{t('calendar.templates.shared')}</Badge>}
              </div>
              {tpl.description && <p className="mt-0.5 truncate text-sm text-muted-foreground">{tpl.description}</p>}
              {tpl.usage_count > 0 && (
                <p className="mt-0.5 text-xs text-muted-foreground">{t('calendar.templates.usedTimes', { count: tpl.usage_count })}</p>
              )}
            </div>
            <div className="flex shrink-0 gap-1.5">
              <Button size="sm" variant="secondary" onClick={() => openDialog(tpl)}>
                {t('common.edit')}
              </Button>
              <Button size="sm" variant="danger" onClick={() => setDeleting(tpl)}>
                {t('common.delete')}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <ModalShell
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? t('calendar.templates.editTemplate') : t('calendar.templates.newTemplate')}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setDialogOpen(false)} disabled={isSaving}>
              {t('common.cancel')}
            </Button>
            <Button size="sm" onClick={save} isLoading={isSaving} disabled={!name.trim()}>
              {editing ? t('common.save') : t('common.create')}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label={t('common.name')} value={name} onChange={(e) => setName(e.target.value)} required />
          <Textarea label={t('common.description')} value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input type="checkbox" checked={isShared} onChange={(e) => setIsShared(e.target.checked)} />
            {t('calendar.templates.shareToggle')}
          </label>
          <div className="space-y-3 rounded-md border border-border p-3">
            <span className="block text-sm font-medium text-foreground">{t('calendar.templates.eventDefaults')}</span>
            <Input label={t('calendar.event.title')} value={eventTitle} onChange={(e) => setEventTitle(e.target.value)} />
            <Select
              label={t('calendar.event.type')}
              value={eventType}
              onChange={(e) => setEventType(e.target.value as EventType)}
              options={EVENT_TYPES.map((v) => ({ value: v, label: t(`calendar.eventType.${v.replace('EVENT_TYPE_', '').toLowerCase()}`) }))}
            />
            <Textarea label={t('calendar.event.description')} value={eventDescription} onChange={(e) => setEventDescription(e.target.value)} rows={2} />
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Input label={t('calendar.event.location')} value={locationName} onChange={(e) => setLocationName(e.target.value)} />
              <Input label={t('calendar.event.conferenceUri')} value={confUri} onChange={(e) => setConfUri(e.target.value)} placeholder="https://…" />
            </div>
          </div>
        </div>
      </ModalShell>

      <ConfirmDialog
        open={!!deleting}
        title={t('calendar.templates.deleteTitle')}
        description={t('calendar.templates.deleteDescription')}
        destructive
        isConfirming={store.isDeleting}
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting?.entity?.id) return;
          try {
            await store.DeleteEventTemplate(deleting.entity.id);
            setDeleting(null);
            load();
          } catch (e) {
            setError(errMessage(e, t('calendar.templates.deleteError')));
            setDeleting(null);
          }
        }}
      />
    </div>
  );
});
